import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Crown,
  FileDown,
  Mic,
  Plus,
  Quote,
  RotateCcw,
} from 'lucide-react';

import { trainingNames } from '../../utils/templates';
import { estimateWorkoutMinutes } from '../../utils/workoutStats';
import { ExerciseRow } from './ExerciseRow';
import { countSets } from './exerciseUtils';

const SwitchRow = ({ Icon, label, checked, onChange }) => (
  <label className="mt-tr-pref">
    <Icon aria-hidden="true" />
    <span>{label}</span>
    <input
      type="checkbox"
      role="switch"
      className="mt-tr-switch"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  </label>
);

/**
 * The workout editor, in the pane where the workout was read (desktop) or as its own page (phones, with a save bar):
 * the letter, the description, the voice and motivation switches, and the exercises as rows that open one at a time.
 * State, data and dialogs live in pages/Workout.jsx.
 */
export const WorkoutEditorView = ({
  isNew,
  isDesktop,
  workoutData,
  takenLetters,
  isSaving,
  isExporting,
  canExport,
  isAIGenerated,
  isPro,
  trialContext,
  openKey,
  library,
  descriptionRef,
  descriptionMissing,
  missingNames,
  hasNoExercises,
  onLetterChange,
  onFieldChange,
  onToggle,
  onExerciseChange,
  onExerciseRemove,
  onSwap,
  onOpenLibrary,
  onExportPdf,
  onSave,
  onCancel,
  onRegenerate,
  onSubscribe,
}) => {
  const { t } = useTranslation();
  const editor = (key, options) => t(`pages.Workout.editor.${key}`, options);
  const { exercises } = workoutData;
  // A workout the AI built on a free account can't be saved: the way to Pro takes the save button's place
  const needsPro = isAIGenerated && !isPro;

  const saveButton = needsPro ? (
    <button
      type="button"
      className={`mt-tr-btn mt-tr-btn--go${isDesktop ? ' mt-tr-btn--sm' : ' mt-tr-btn--big'}`}
      onClick={onSubscribe}
    >
      <Crown aria-hidden="true" />
      {t('pages.Workout.subscribeToPro', { context: trialContext })}
    </button>
  ) : (
    <button
      type="button"
      className={`mt-tr-btn mt-tr-btn--go${isDesktop ? ' mt-tr-btn--sm' : ' mt-tr-btn--big'}`}
      onClick={onSave}
      disabled={isSaving}
    >
      {isSaving ? editor('saving') : isNew ? editor('create') : editor('save')}
    </button>
  );

  const titleText = isNew
    ? editor('titleNew')
    : editor('titleEditLetter', { name: workoutData.name });

  return (
    <div className="mt-tr-editor">
      {isDesktop ? (
        <div className="mt-tr-editor__bar">
          <h2 className="mt-tr-editor__title">{titleText}</h2>
          <div className="mt-tr-editor__acts">
            {canExport && (
              <button
                type="button"
                className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--sm"
                onClick={onExportPdf}
                disabled={isExporting}
              >
                <FileDown aria-hidden="true" />
                {editor('exportPdf')}
              </button>
            )}
            <button
              type="button"
              className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--sm"
              onClick={onCancel}
            >
              {editor('cancel')}
            </button>
            {saveButton}
          </div>
        </div>
      ) : (
        <header className="mt-tr-bar mt-tr-bar--title">
          <button
            type="button"
            className="mt-tr-bar__back"
            onClick={onCancel}
            aria-label={editor('cancel')}
          >
            <ArrowLeft aria-hidden="true" />
          </button>
          <h1 className="mt-tr-bar__title">
            {isNew ? editor('titleNew') : editor('titleEdit')}
          </h1>
          <span className="mt-tr-bar__end">
            {canExport && (
              <button
                type="button"
                className="mt-tr-icon-btn mt-tr-icon-btn--flat"
                onClick={onExportPdf}
                disabled={isExporting}
                aria-label={editor('exportPdf')}
              >
                <FileDown aria-hidden="true" />
              </button>
            )}
          </span>
        </header>
      )}

      {isAIGenerated && (
        <div className="mt-tr-ai-note">
          <span>{editor('aiNote')}</span>
          <button
            type="button"
            className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--xs"
            onClick={onRegenerate}
          >
            {isPro && <RotateCcw aria-hidden="true" />}
            {isPro ? editor('aiAgain') : editor('aiManual')}
          </button>
        </div>
      )}

      <div className="mt-tr-ident">
        <div className="mt-tr-ident__wide">
          <fieldset className="mt-tr-letters">
            <legend className="mt-tr-label">{editor('letter')}</legend>
            <div className="mt-tr-letters__row">
              {trainingNames.map((letter) => {
                const isTaken = takenLetters.includes(letter);
                return (
                  <label key={letter}>
                    <input
                      type="radio"
                      name="workout-letter"
                      value={letter}
                      checked={workoutData.name === letter}
                      disabled={isTaken}
                      onChange={() => onLetterChange(letter)}
                    />
                    <span aria-hidden="true">{letter}</span>
                    <span className="mt-sr-only">
                      {letter}
                      {isTaken ? `, ${editor('letterTaken')}` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
        <div>
          <label className="mt-tr-label" htmlFor="workout-description">
            {editor('descriptionLabel')}
          </label>
          <input
            ref={descriptionRef}
            id="workout-description"
            className="mt-tr-field"
            value={workoutData.description ?? ''}
            placeholder={editor('descriptionPlaceholder')}
            autoComplete="off"
            aria-invalid={descriptionMissing || undefined}
            aria-describedby={
              descriptionMissing ? 'workout-description-error' : undefined
            }
            onChange={(event) =>
              onFieldChange('description', event.target.value)
            }
          />
          {descriptionMissing && (
            <p id="workout-description-error" className="mt-tr-field-error">
              {t('notifications.emptyWorkoutDesc')}
            </p>
          )}
        </div>
        <div className="mt-tr-prefs-box">
          <SwitchRow
            Icon={Mic}
            label={editor('voice')}
            checked={!!workoutData.voiceInstructions}
            onChange={(checked) => onFieldChange('voiceInstructions', checked)}
          />
          <SwitchRow
            Icon={Quote}
            label={editor('motivation')}
            checked={!!workoutData.motivation}
            onChange={(checked) => onFieldChange('motivation', checked)}
          />
        </div>
      </div>

      <section aria-labelledby="mt-tr-editor-exercises">
        <div className="mt-tr-ex-head">
          <h3 id="mt-tr-editor-exercises">
            {editor('exercises')}
            {exercises.length > 0 && (
              <small>
                {t('pages.Workouts.list.setCount', {
                  count: countSets(exercises),
                })}{' '}
                ·{' '}
                {t('pages.Workouts.list.minutes', {
                  count: estimateWorkoutMinutes(exercises),
                })}
              </small>
            )}
          </h3>
          <button
            type="button"
            className="mt-tr-text-btn"
            onClick={() => onOpenLibrary('add')}
          >
            <Plus aria-hidden="true" />
            {editor('add')}
          </button>
        </div>
        <ol className="mt-tr-erows">
          {exercises.map((exercise, index) => (
            <ExerciseRow
              key={exercise.uiKey ?? `position-${index}`}
              index={index}
              exercise={exercise}
              isOpen={(exercise.uiKey ?? `position-${index}`) === openKey}
              isNameMissing={missingNames && !exercise.name?.trim()}
              canRemove={exercises.length > 1}
              library={library}
              onToggle={onToggle}
              onChange={onExerciseChange}
              onRemove={onExerciseRemove}
              onSwap={onSwap}
            />
          ))}
        </ol>
        {missingNames &&
          exercises.some((exercise) => !exercise.name?.trim()) && (
            <p className="mt-tr-field-error">
              {t('notifications.emptyExerciseName')}
            </p>
          )}
        {hasNoExercises && (
          <p className="mt-tr-field-error">{editor('noExercises')}</p>
        )}
        <button
          type="button"
          className="mt-tr-add-tile"
          onClick={() => onOpenLibrary('add')}
        >
          <Plus aria-hidden="true" />
          {editor('addFromLibrary')}
        </button>
      </section>

      {!isDesktop && <div className="mt-tr-savebar">{saveButton}</div>}
    </div>
  );
};
